import { FC, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { FullMeetingFragment, MeetingStepStatus, MeetingStepViewId, SkillEmployeeStatusDto } from '../../../../types'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { AppraisalWelcomeTeaser } from '../shared/AppraisalWelcomeTeaser'
import { MeetingsHistoryTableContainer } from '../../../meeting/shared/MeetingsHistoryTableContainer'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { PerAtAGlance } from '../../../../components/custom_components/AtAGlance/AtAGlance'
import { PerAtAGlanceElement } from '../../../../components/custom_components/AtAGlance/AtAGlanceElement'
import { GecoAtAGlance } from '../shared/Geco/GecoAtAGlance'
import { Divider, Card } from 'antd'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { isAppraisalUpdate } from '../meeting/util/meetingUtil'
import PerText from '../../../../components/custom_components/Text'
import { usePersonalSkillStatusInMeetingPreparation } from '../../../../graphql/skill-status/useSkillStatusQueries'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import { refreshMySkillStatusInEmployeePreparation } from '../../../../graphql/skill-status/useSkillStatusMutations'
import { SkillStatusLabelComponent, SkillStatusDescription } from '../../../skillStatus/SkillStatusLabelComponent'
import { EmployeeActivityListComponent } from '../../../badge/EmployeeActivityListComponent'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}

const EmployeeMeetingStepOverview: FC<Props> = ({ meeting, nextBtn }) => {
  const [t] = useTranslation()
  const [isRefreshMySkillStatusProcessing, toggleRefreshMySkillStatusProcessing] = useState<boolean>(false)
  const [skillStatusError, setSkillStatusError] = useState<boolean>(false)

  const { data, loading, error } = usePersonalSkillStatusInMeetingPreparation(meeting.employee.id)
  if (!data || loading || error) {
    return <LoadingAndErrors hasNoData={!data} isLoading={loading} error={error} />
  }
  const skillStatus: SkillEmployeeStatusDto | null | undefined = data.getPersonalSkillStatusInEmployeePreparation

  return (
    <>
      <MeetingBackground>
        <AppraisalWelcomeTeaser
          subtitle={t('appraisalMeeting.stepOverview.pageDescriptionSubtitleEmployee')}
          info={t('appraisalMeeting.stepOverview.pageDescriptionEmployee')}
        />

        <PerText appearance="tile-heading" mb="s">
          {t('appraisalMeeting.stepOverview.skillCenterEmployee.header')}
        </PerText>

        <Card bordered={false}>
          <PerFlexRow align="middle">
            <PerText mb="xxs">
              <Trans
                i18nKey="appraisalMeeting.stepOverview.skillCenterEmployee.desc"
                components={[
                  <a
                    style={{ textDecoration: 'underline', textTransform: 'uppercase', fontWeight: 'bold' }}
                    href={`${meeting.employee.skillCenterUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {''}
                  </a>,
                ]}
              />
            </PerText>
          </PerFlexRow>
          <SkillStatusLabelComponent
            skillStatus={skillStatus}
            isProcessing={isRefreshMySkillStatusProcessing}
            skillStatusErrorVisible={skillStatusError}
          />
        </Card>

        <SkillStatusDescription
          skillStatus={skillStatus}
          skillStatusErrorVisible={skillStatusError}
          onClick={async () => {
            toggleRefreshMySkillStatusProcessing(true)
            const { data: skillStatusData, errors: skillStatusErrors } = await refreshMySkillStatusInEmployeePreparation(
              meeting.employee.id,
            )
            if (!skillStatusData && skillStatusErrors) {
              setSkillStatusError(true)
            }
            toggleRefreshMySkillStatusProcessing(false)
          }}
          isProcessing={isRefreshMySkillStatusProcessing}
        />

        {meeting.activityListEnabled && (
          <>
            <Divider />
            <EmployeeActivityListComponent
              employeeId={meeting.employee.id}
              title={t('appraisalMeeting.stepOverview.activityList.header')}
              lastYear
            />
          </>
        )}

        <Divider />

        <PerAtAGlance>
          <PerAtAGlanceElement
            icon="project_stroke"
            heading={t('appraisalMeeting.stepOverview.pageDescriptionProjects.titleGecoInfo')}
            info={t('appraisalMeeting.stepOverview.pageDescriptionProjects.subtitleGecoInfo')}
          >
            <GecoAtAGlance
              employeeId={meeting.employee.id}
              projectDisplayLimit={3}
              as={meeting.appraisalSeason}
              appraisalUpdate={isAppraisalUpdate(meeting)}
            />
          </PerAtAGlanceElement>
        </PerAtAGlance>

        <Divider />

        <MeetingsHistoryTableContainer employeeId={meeting.employee.id} emptyTextType="meetingEmployee" />
      </MeetingBackground>

      <PerFlexRow justify="space-between">
        <div>&nbsp;</div>
        {nextBtn && nextBtn(MeetingStepStatus.Done)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Intro} stepStatus={MeetingStepStatus.Done} />
    </>
  )
}
export default EmployeeMeetingStepOverview
